import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Edit from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { MixpanelEventName } from 'utils/constants';
import Theme from 'theme';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { mainNav } from 'utils/spaUrls';
import { ReactComponent as GroupIcon } from 'assets/GroupIcon.svg';
import { useHttp } from 'hooks/use-fetch';
import ContentLoader from 'components/Common/ContentLoader';
import HLButton from 'components/HLButton';
import HLCard from 'components/HLCard';

export function clampPercent(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

export type DashboardStatsCardProps = {
  isPatientStatsLoading: boolean;
  openStepModal: () => void;
  setShowEditGoalDrawer: (show: boolean) => void;
  addPatientGoal: number;
  activatedPatients: number;
  pendingPatients: number;
  percentageWithHealthRecords: number;
  percentageWithPercentageEngaged: number;
};

function DashboardStatsCard(props: DashboardStatsCardProps) {
  const navigator = useNavigate();
  const isMobile = useMediaQuery(Theme.breakpoints.down('md'));
  const { superAdminId } = useHttp();
  const {
    isPatientStatsLoading,
    openStepModal,
    setShowEditGoalDrawer,
    addPatientGoal,
    activatedPatients,
    pendingPatients,
    percentageWithHealthRecords,
    percentageWithPercentageEngaged,
  } = props;
  const isSuperAdmin = !!superAdminId;
  const totalPatients = Math.min(activatedPatients + pendingPatients, Number(addPatientGoal));

  const renderProgressBar = () => {
    const total = Number(addPatientGoal);
    const activatedPercent = total > 0 ? (activatedPatients / total) * 100 : 0;
    const pendingPercent = total > 0 ? (pendingPatients / total) * 100 : 0;

    return (
      <Box sx={{ width: '100%', mt: 1, mb: 3 }}>
        <Box sx={{ width: '100%', height: 18, borderRadius: 1.5, overflow: 'hidden', position: 'relative', background: '#D2D2D2' }}>
          <Box sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${activatedPercent}%`,
            background: 'linear-gradient(270deg, #1769A2 88.32%, #1D4865 100%)',
            borderRadius: activatedPercent === 100 ? 6 : '6px 0 0 6px',
            transition: 'width 0.5s',
          }}
          />
          <Box sx={{
            position: 'absolute',
            left: `${activatedPercent}%`,
            top: 0,
            height: '100%',
            width: `${pendingPercent}%`,
            background: '#57CBFF',
            borderRadius: pendingPercent === 100 ? 6 : 0,
            transition: 'width 0.5s',
          }}
          />
        </Box>
      </Box>
    );
  };

  const handleInfoClick = React.useCallback(() => {
    openStepModal();
    trackMixpanelEvent(
      MixpanelEventName.DASHBOARD_PATIENT_ACTIVATION_INFO_ICON_CLICKED,
      { section: 'Patient activation progress' },
    );
  }, [openStepModal]);

  const handleEditGoalClick = React.useCallback(() => {
    setShowEditGoalDrawer(true);
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_EDIT_GOAL_BUTTON_CLICKED);
  }, [setShowEditGoalDrawer]);

  const actionButtons = (
    <Box
      flex={1}
      justifyContent='flex-end'
      display='flex'
      gap={2}
      width={isMobile ? '100%' : 'auto'}
      flexDirection={{
        xs: 'column',
        md: 'row',
      }}
    >
      <HLButton
        startIcon={<Edit />}
        onClick={handleEditGoalClick}
        variant='outlined'
        size='medium'
        disabled={isSuperAdmin}
      >
        Edit Goal
      </HLButton>
      <HLButton
        color='primary'
        variant='contained'
        size='medium'
        type='submit'
        startIcon={<PersonAddIcon />}
        onClick={() => {
          navigator(mainNav.addNewPatient);
          trackMixpanelEvent(MixpanelEventName.DASHBOARD_ADD_PATIENTS_BUTTON_CLICKED, {
            section: 'Patient activation progress',
          });
        }}
      >
        Add Patients
      </HLButton>
    </Box>
  );

  return (
    <HLCard
      sx={{
        p: 2,
        position: 'relative',
      }}
    >
      <ContentLoader minHeight={272} isError={false} isFetching={isPatientStatsLoading}>
        <>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                width: '100%',
              }}
              gap={1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant='h6'
                  fontSize={24}
                  fontWeight={600}
                  sx={{ color: Theme.custom.colors.lightTextPrimary }}
                >
                  Patient activation progress
                </Typography>
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: Theme.custom.colors.lightPrimaryMain,
                    cursor: 'pointer',
                  }}
                  onClick={handleInfoClick}
                />
              </Box>
              {!isMobile && actionButtons}
            </Box>
            <Typography
              variant='body1'
              sx={{ color: Theme.custom.colors.lightTextSecondary, mb: 2 }}
            >
              You&apos;ve achieved
              {' '}
              {clampPercent(Math.round((totalPatients / Number(addPatientGoal || 1)) * 100))}
              % of
              your goal
            </Typography>
          </Box>
          {/* Progress bar */}
          {renderProgressBar()}
          {/* Stats row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mt: 2.5,
              mb: 1,
            }}
            gap={3}
          >
            {/* Left: Activated */}
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
              flex={1}
              justifyContent='flex-start'
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  border: '2px solid #01579B',
                  borderRadius: '50%',
                  minWidth: 35,
                  height: 35,
                  boxSizing: 'border-box',
                  background: '#01579B',
                }}
              >
                <GroupIcon fontSize={26} />
              </Box>
              <Box>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 600, color: Theme.custom.colors.lightTextPrimary }}
                >
                  {activatedPatients}
                  {' '}
                  patients activated
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ color: Theme.custom.colors.lightTextSecondary, mt: 0.5 }}
                >
                  •
                  {' '}
                  {clampPercent(percentageWithHealthRecords)}
                  % have health records
                  <br />
                  •
                  {' '}
                  {clampPercent(percentageWithPercentageEngaged)}
                  % are engaged on app
                </Typography>
              </Box>
            </Box>
            {/* Center: Pending sign up */}
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
              flex={1}
              justifyContent='center'
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  border: '2px solid #30B3EE',
                  borderRadius: '50%',
                  minWidth: 35,
                  height: 35,
                  boxSizing: 'border-box',
                  background: '#30B3EE',
                }}
              >
                <AccessTimeIcon sx={{ color: '#FFFFFF', fontSize: 28 }} />
              </Box>
              <Typography
                variant='body1'
                sx={{ fontWeight: 600, color: Theme.custom.colors.lightTextPrimary }}
              >
                {pendingPatients}
                {' '}
                patients pending sign up
              </Typography>
            </Box>
            {/* Right: Patients to add + Edit Goal */}
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
              flex={1}
              justifyContent='flex-end'
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  border: '2px solid #D2D2D2',
                  borderRadius: '50%',
                  minWidth: 35,
                  height: 35,
                  boxSizing: 'border-box',
                  background: '#D2D2D2',
                }}
              >
                <PersonAddIcon sx={{ color: '#FFFFFF', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 600, color: Theme.custom.colors.lightTextPrimary, mb: 1 }}
                >
                  {Number(addPatientGoal) - totalPatients}
                  {' '}
                  patients to add
                </Typography>
              </Box>
            </Box>
          </Box>
          {isMobile && actionButtons}
        </>
      </ContentLoader>
    </HLCard>
  );
}

export default DashboardStatsCard;
